// ============================================================================
// CANDIDATE SEARCH SERVICES
// ============================================================================

import { supabase } from '../utils/supabase';
import { escapePostgresLike } from '../utils/sqlSecurity';

// Helper function to calculate match score between candidate and job
// EXPORTED for use in other components for consistency
export function calculateMatchScore(candidate, job) {
  let score = 0;
  
  // Match based on skills overlap
  if (job.skills && Array.isArray(job.skills) && candidate.skills && Array.isArray(candidate.skills)) {
    const matchingSkills = job.skills.filter(skill => 
      candidate.skills.some(candidateSkill => 
        candidateSkill.toLowerCase() === skill.toLowerCase()
      )
    );
    
    // Award points based on percentage of matching skills
    if (job.skills.length > 0) {
      score += (matchingSkills.length / job.skills.length) * 70; // Up to 70 points for skills match
    }
  }
  
  // Add points for experience (up to 30 points)
  if (candidate.experience_years) {
    // More experience = higher score, but with diminishing returns after 5 years
    score += Math.min(30, candidate.experience_years * 6);
  }
  
  // Normalize to 0-100 scale
  return Math.min(100, Math.round(score));
}

export const candidateService = {
  // Search candidates with comprehensive filtering
  searchCandidates: async (filters = {}) => {
    try {
      console.log('Search filters received:', filters);
      
      // Build the base query with proper column selection
      let query = supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          title,
          bio,
          skills,
          experience_level,
          experience_years,
          hourly_rate,
          location,
          timezone,
          availability_status,
          languages,
          is_verified,
          is_active,
          profile_picture,
          created_at,
          updated_at
        `)
        .eq('role', 'va')
        .eq('is_active', true)
        .not('email', 'eq', 'test.admin@vasprouts.com') // Exclude the test admin user specifically
        .not('email', 'ilike', '%admin%') // Exclude any other admin emails
        .not('full_name', 'ilike', '%admin%'); // Exclude admin names

      // Apply text search across multiple fields
      if (filters.query) {
        const searchTerm = escapePostgresLike(filters.query.trim());
        query = query.or(`full_name.ilike.%${searchTerm}%,title.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%,location.ilike.%${searchTerm}%`);
      }

      // Apply location filter (case insensitive)
      if (filters.location) {
        const escapedLocation = escapePostgresLike(filters.location);
        query = query.ilike('location', `%${escapedLocation}%`);
      }

      // Apply skills filter (array overlap) using single RPC call
      if (filters.skills && Array.isArray(filters.skills) && filters.skills.length > 0) {
        const validSkills = filters.skills.filter(s => s && s.trim()).map(s => s.trim());
        if (validSkills.length > 0) {
          try {
            const { data: matchingUserIds, error: rpcError } = await supabase
              .rpc('search_candidates_by_skills', { skill_array: validSkills });

            if (rpcError) throw rpcError;

            if (matchingUserIds && matchingUserIds.length > 0) {
              query = query.in('id', matchingUserIds);
            } else {
              query = query.eq('id', '00000000-0000-0000-0000-000000000000');
            }
          } catch (error) {
            console.error('Skills filter error:', error);
            // Continue without skills filter on error
          }
        }
      }

      // Apply experience level filter
      if (filters.experience_level) {
        query = query.eq('experience_level', filters.experience_level);
      }

      // Apply hourly rate filters
      if (filters.hourly_rate_min && !isNaN(parseFloat(filters.hourly_rate_min))) {
        query = query.gte('hourly_rate', parseFloat(filters.hourly_rate_min));
      }
      if (filters.hourly_rate_max && !isNaN(parseFloat(filters.hourly_rate_max))) {
        query = query.lte('hourly_rate', parseFloat(filters.hourly_rate_max));
      }

      // Apply availability filter
      if (filters.availability) {
        query = query.eq('availability_status', filters.availability);
      }

      // Apply minimum experience years (for rating_min, we'll use experience_years as proxy)
      if (filters.rating_min && !isNaN(parseFloat(filters.rating_min))) {
        const minExperience = Math.floor(parseFloat(filters.rating_min));
        query = query.gte('experience_years', minExperience);
      }

      // Apply verified filter
      if (filters.verified_only) {
        query = query.eq('is_verified', true);
      }

      // Apply active filter (already applied above but keeping for consistency)
      if (filters.active_only) {
        query = query.eq('is_active', true);
      }

      // Apply sorting
      const sortBy = filters.sort_by || 'updated_at';
      const sortOrder = filters.sort_order === 'asc' ? { ascending: true } : { ascending: false };
      
      // Map to valid database columns
      const validSortColumns = {
        'rating': 'experience_years', // Use experience as proxy for rating
        'created_at': 'created_at',
        'updated_at': 'updated_at',
        'hourly_rate': 'hourly_rate',
        'experience_years': 'experience_years'  
      };
      
      const dbSortColumn = validSortColumns[sortBy] || 'updated_at';
      query = query.order(dbSortColumn, sortOrder);

      // Apply pagination
      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
      } else if (filters.limit) {
        query = query.limit(filters.limit);
      }

      console.log('Executing candidate search query...');
      
      try {
        const { data, error, count } = await query;
        
        if (error) {
          console.error('Database query error:', error);
          throw error;
        }

        const candidateCount = data?.length || 0;
        console.log(`Found ${candidateCount} candidates from database`);
        
        // If no candidates found, return empty result with proper structure
        if (candidateCount === 0) {
          return { 
            candidates: [], 
            total: 0,
            hasMore: false,
            error: null 
          };
        }
        
        // Transform data to match expected format
        const transformedCandidates = (data || []).map(candidate => ({
          ...candidate,
          // Ensure skills is always an array
          skills: Array.isArray(candidate.skills) ? candidate.skills : [],
          // Ensure languages is always an array  
          languages: Array.isArray(candidate.languages) ? candidate.languages : [],
          // Add computed fields for UI compatibility
          rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)), // Computed rating
          reviews_count: Math.floor(candidate.experience_years * 10), // Computed reviews
          response_rate: '95%', // Default response rate
          success_rate: '96%', // Default success rate
          completed_jobs: Math.floor(candidate.experience_years * 20), // Computed completed jobs
          availability: candidate.availability_status || 'available'
        }));

        // Calculate total count and pagination info
        const total = count || transformedCandidates.length;
        const hasMore = filters.offset ? 
          (filters.offset + transformedCandidates.length < total) : 
          (transformedCandidates.length >= (filters.limit || 10));

        return { 
          candidates: transformedCandidates, 
          total,
          hasMore,
          error: null 
        };
      } catch (queryError) {
        // If the complex query fails, fall back to a simpler query
        console.warn('Complex search failed, falling back to basic search:', queryError);
        
        try {
          // Try a much simpler query without the complex filters
          const fallbackQuery = supabase
            .from('user_profiles')
            .select('*')
            .eq('role', 'va')
            .eq('is_active', true)
            .limit(filters.limit || 10);
            
          // Add name search if provided
          if (filters.query) {
            const escapedQuery = escapePostgresLike(filters.query);
            fallbackQuery.ilike('full_name', `%${escapedQuery}%`);
          }
          
          const { data: fallbackData, error: fallbackError } = await fallbackQuery;
          
          if (fallbackError) throw fallbackError;
          
          console.log(`Fallback search returned ${fallbackData.length} results`);
          
          // Transform even the fallback data
          const transformedFallback = (fallbackData || []).map(candidate => ({
            ...candidate,
            skills: Array.isArray(candidate.skills) ? candidate.skills : [],
            languages: Array.isArray(candidate.languages) ? candidate.languages : [],
            rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)),
            reviews_count: Math.floor(candidate.experience_years * 10),
            response_rate: '95%',
            success_rate: '96%',
            completed_jobs: Math.floor(candidate.experience_years * 20),
            availability: candidate.availability_status || 'available'
          }));
          
          return {
            candidates: transformedFallback,
            total: transformedFallback.length,
            hasMore: false,
            error: null
          };
        } catch (fallbackError) {
          // If even the fallback fails, return the original error
          console.error('Search candidates error:', queryError);
          return { 
            candidates: [], 
            total: 0,
            hasMore: false,
            error: queryError.message 
          };
        }
      }
    } catch (error) {
      console.error('Search candidates error:', error);
      return { 
        candidates: [], 
        total: 0,
        hasMore: false,
        error: error.message 
      };
    }
  },

  // Get candidate profile by ID
  getCandidateById: async (candidateId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', candidateId)
        .eq('role', 'va')
        .single();
      
      if (error) throw error;
      return { candidate: data, error: null };
    } catch (error) {
      console.error('Get candidate by ID error:', error);
      return { candidate: null, error: error.message };
    }
  },

  // Get AI recommendations for a job
  getAIRecommendations: async (jobId) => {
    try {
      // First fetch the job details to get skills
      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('skills, title, description')
        .eq('id', jobId)
        .single();
        
      if (jobError) throw jobError;
      
      // If no job found or no skills defined, return empty result
      if (!job || !job.skills || !Array.isArray(job.skills) || job.skills.length === 0) {
        return { candidates: [], total: 0, error: 'No skills defined for this job' };
      }
      
      // Create a query to find candidates with matching skills
      const query = supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'va')
        .eq('is_active', true);
        
      // Use the skills contains operation for each skill
      // Temporary workaround for array operations
      let matchingUserIds = new Set();
      let isFirstSkill = true;
      
      for (const skill of job.skills) {
        if (skill && skill.trim()) {
          try {
            const { data: skillMatches, error: skillError } = await supabase
              .from('user_profiles')
              .select('id')
              .eq('role', 'va')
              .eq('is_active', true)
              .filter('skills', 'cs', `["${skill.trim()}"]`);
              
            if (skillError) throw skillError;
            
            // For the first skill, initialize the set. For subsequent skills, find common IDs
            if (isFirstSkill) {
              skillMatches.forEach(user => matchingUserIds.add(user.id));
              isFirstSkill = false;
            } else {
              // For an "OR" match logic, just add all IDs
              skillMatches.forEach(user => matchingUserIds.add(user.id));
            }
          } catch (error) {
            console.error(`Error matching skill ${skill}:`, error);
          }
        }
      }
      
      // If we have matching users, fetch their full profiles
      if (matchingUserIds.size > 0) {
        const { data: candidates, error: candidateError } = await supabase
          .from('user_profiles')
          .select('*')
          .in('id', Array.from(matchingUserIds))
          .order('experience_years', { ascending: false })
          .limit(10);
          
        if (candidateError) throw candidateError;
        
        // Transform the candidates similar to the search function
        const transformedCandidates = candidates.map(candidate => ({
          ...candidate,
          skills: Array.isArray(candidate.skills) ? candidate.skills : [],
          languages: Array.isArray(candidate.languages) ? candidate.languages : [],
          rating: Math.min(5, Math.max(1, candidate.experience_years * 0.8 + 3.5)),
          match_score: calculateMatchScore(candidate, job)
        }));
        
        // Sort by match score
        transformedCandidates.sort((a, b) => b.match_score - a.match_score);
        
        return { candidates: transformedCandidates, total: transformedCandidates.length, error: null };
      } else {
        return { candidates: [], total: 0, error: null };
      }
    } catch (error) {
      console.error('Get AI recommendations error:', error);
      return { candidates: [], total: 0, error: error.message };
    }
  }
};
